import { useQuery } from "@tanstack/react-query";
import { createFileRoute } from "@tanstack/react-router";
import { PageHeader } from "@/components/page-header";
import { EmptyState, ErrorState, LoadingState } from "@/components/states";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { queries } from "@/lib/queries";
import { formatDate } from "@/lib/utils";

export const Route = createFileRoute("/app/$workspaceId/auditoria")({
  component: AuditoriaPage,
});

const ACTION_LABELS: Record<string, string> = {
  "workspace.criado": "Workspace criado",
  "workspace.atualizado": "Workspace atualizado",
  "workspace.excluido": "Workspace excluído",
  "convite.enviado": "Convite enviado",
  "convite.reenviado": "Convite reenviado",
  "convite.revogado": "Convite cancelado",
  "convite.aceito": "Convite aceito",
  "membro.papel_alterado": "Papel de membro alterado",
  "membro.removido": "Membro removido",
  "membro.saiu": "Membro saiu",
  "meta.conexao_criada": "Conta Meta conectada",
  "meta.conexao_reconectada": "Conta Meta reconectada",
  "meta.conexao_desconectada": "Conta Meta desconectada",
  "meta.conexao_expirada": "Conexão Meta expirada",
  "meta.conexao_revogada_pela_meta": "Acesso removido pela pessoa na Meta",
  "meta.sincronizacao_solicitada": "Sincronização solicitada",
  "meta.sincronizacao_concluida": "Sincronização concluída",
  "meta.sincronizacao_falhou": "Sincronização falhou",
  "meta.exclusao_dados_recebida": "Pedido de exclusão de dados (Meta)",
};

function resumo(value: unknown): string {
  if (value === null || value === undefined) return "";
  if (typeof value === "object") {
    return Object.entries(value as Record<string, unknown>)
      .map(([k, v]) => `${k}: ${typeof v === "string" ? v : JSON.stringify(v)}`)
      .join(" · ");
  }
  return typeof value === "string" ? value : JSON.stringify(value);
}

function AuditoriaPage() {
  const { workspaceId } = Route.useParams();
  const logs = useQuery(queries.auditLogs(workspaceId));

  return (
    <>
      <PageHeader
        title="Auditoria"
        description="Tudo que foi feito neste workspace: quem, quando e o quê."
        actions={
          <Button
            variant="outline"
            onClick={() => void logs.refetch()}
            loading={logs.isFetching && !logs.isPending}
          >
            Atualizar
          </Button>
        }
      />
      <Card>
        <CardContent>
          {logs.isPending ? (
            <LoadingState rows={5} />
          ) : logs.isError ? (
            <ErrorState error={logs.error} onRetry={() => void logs.refetch()} />
          ) : logs.data.items.length === 0 ? (
            <EmptyState
              title="Nada registrado ainda"
              description="As ações do workspace aparecem aqui assim que acontecerem."
            />
          ) : (
            <ul className="divide-y">
              {logs.data.items.map((log) => (
                <li
                  key={log.id}
                  className="flex flex-col gap-1 py-3 sm:flex-row sm:items-start sm:gap-4"
                >
                  <time
                    dateTime={log.createdAt}
                    className="shrink-0 text-xs text-muted-foreground sm:w-32 sm:pt-0.5"
                  >
                    {formatDate(log.createdAt)}
                  </time>
                  <div className="min-w-0 flex-1 space-y-1">
                    <div className="flex flex-wrap items-center gap-2">
                      <span className="text-sm font-medium">
                        {ACTION_LABELS[log.action] ?? log.action}
                      </span>
                      <Badge variant="outline">{log.actorName ?? "Sistema"}</Badge>
                    </div>
                    {log.before !== null && log.before !== undefined ? (
                      <p className="truncate text-xs text-muted-foreground">
                        Antes: {resumo(log.before)}
                      </p>
                    ) : null}
                    {log.after !== null && log.after !== undefined ? (
                      <p className="truncate text-xs text-muted-foreground">
                        Depois: {resumo(log.after)}
                      </p>
                    ) : null}
                  </div>
                </li>
              ))}
            </ul>
          )}
          {logs.data?.nextCursor ? (
            <p className="mt-4 text-center text-xs text-muted-foreground">
              Mostrando os 50 registros mais recentes.
            </p>
          ) : null}
        </CardContent>
      </Card>
    </>
  );
}
